import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Boxes, ArrowRight, ShieldCheck, Zap, BarChart3, AlertCircle } from 'lucide-react';
import { loginThunk, selectAuthLoading, selectAuthError, selectIsLoggedIn, clearError } from '../store/authSlice';

export default function Login() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const isLoading  = useSelector(selectAuthLoading);
  const error      = useSelector(selectAuthError);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => { if (isLoggedIn) navigate('/dashboard', { replace: true }); }, [isLoggedIn, navigate]);
  useEffect(() => { return () => dispatch(clearError()); }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(loginThunk({ email, password }));
    if (loginThunk.fulfilled.match(result)) navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#1E3A5F]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] via-[#1E3A5F]/90 to-[#2E6DA4]/50" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-md bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <div className="text-lg font-semibold font-heading">SlipSense</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300">Payroll OS</div>
            </div>
          </div>
          <div className="space-y-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300 mb-3">For Indian SMEs</div>
              <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight font-heading leading-tight">
                Run payroll in<br />one click.
              </h1>
              <p className="mt-4 text-slate-300 text-base max-w-md">
                Replace spreadsheets with structured payroll. PF, ESI, PT, and payslips — done in minutes.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 max-w-sm">
              {[
                { icon: Zap,         label: '3-step monthly payroll wizard' },
                { icon: ShieldCheck, label: 'PF, ESI & PT compliance built-in' },
                { icon: BarChart3,   label: 'Live expense analytics' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-200">
                  <div className="w-8 h-8 rounded-md bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
                    <f.icon className="w-4 h-4" />
                  </div>
                  {f.label}
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-400">© 2026 SlipSense · Made for small business owners</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-md bg-[#1E3A5F] flex items-center justify-center">
              <Boxes className="w-5 h-5 text-white" />
            </div>
            <div className="text-lg font-semibold font-heading">SlipSense</div>
          </div>

          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Welcome back</div>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-heading">Sign in to your workspace</h2>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 mb-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.in"
                required
                className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-600">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[#2E6DA4] hover:bg-[#255a88] disabled:opacity-60 text-white font-medium rounded-md flex items-center justify-center gap-2 transition-colors"
            >
              {isLoading ? 'Signing in…' : <>Sign In <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-500 text-center">
            New here?{' '}
            <Link to="/register" className="text-[#2E6DA4] font-medium hover:underline">Create an account</Link>
          </div>
          <div className="mt-4 text-sm text-slate-500 text-center">
            <Link to="/pricing" className="text-[#2E6DA4] font-medium hover:underline">View pricing plans →</Link>
          </div>
          <div className="mt-6 px-4 py-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-500">
            <span className="font-medium text-slate-700">Demo:</span> Use <code>demo@slipsense.in</code> / <code>Demo@12345</code> after running seed.
          </div>
        </div>
      </div>
    </div>
  );
}